import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import TopBar from '../Layout/TopBar';
import NotificationBar from '../Layout/NotificationsBar';
import NewFriendsConnection from '../Layout/NewFriends';
import FriendRecommendations from '../Layout/FriendRecommendations';
import PromoBanner from '../Layout/PromoBanner';
import CommunityCard from '../Layout/CommunityCard';
import HealthGoalModal from '../Layout/healthGoalModal';
import Greeting from '../Layout/Greeting';
import LandingActionButtons from '../Layout/LandingActionButtons';
import DailyQuote from '../Layout/DailyQuote';
import ResponsiveNewFriendsConnection from '../Layout/TrendingPostsWithSideBar';
import ResponsiveFoodPost from '../Layout/ResponsiveFoodPost';
import ArticleModal from '../Layout/ArticleModal';
import BannerModal from '../Layout/BannerModal';
import Banner from '../Layout/Banners';
import LandingPageWidgets from '../Layout/LandingPageWidgets';
import InterestMedia from '../Layout/InterestMedia';
import InterestGroups from '../Layout/InterestGroups';
import FriendsConnection from '../Layout/FriendsConnection';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserContext } from '../../contexts/UserContext';


import {
  Box,
  Button,
  Container,
  CssBaseline,
  Divider,
  Grid,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
  TextField,
  Autocomplete,
  CardContent,
  Card,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';

import { get,  } from '../../utility/fetchWeb';
import { get as gets, post, put } from '../../utility/fetchWeb';
import notification from '../../utility/notification';

const PageContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  minHeight: '100vh',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(6),
  },
}));

const MainContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
}));

const TopSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const MidSection = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(4),
  width: '100%',
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(8),
  },
}));

const PromoSection = styled(Box)(({ theme }) => ({
  width: '100%',
  py: theme.spacing(4),
  px: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    px: theme.spacing(4),
  },
  [theme.breakpoints.up('lg')]: {
    px: theme.spacing(8),
  },
}));

const CommunitySection = styled(Box)(({ theme }) => ({
  gap: theme.spacing(8),
}));

const CommunityGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '3fr',
  gap: theme.spacing(),
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(1, 1fr)',
    gap: theme.spacing(8),
  },
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(8),
  },
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing(4),
  },
}));

// Error boundary to recover from DOM mutations (e.g., Google Translate) on revisit
class LandingErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { remountKey: 0 };
  }
  static getDerivedStateFromError() {
    // Force remount by changing key
    return { remountKey: Date.now() };
  }
  componentDidCatch(error, info) {
    console.error('Landing page recovered from a DOM commit error:', error, info);
  }
  render() {
    return <React.Fragment key={this.state.remountKey}>{this.props.children}</React.Fragment>;
  }
}

const HomePage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isArticleModalOpen, setIsArticleModalOpen] = useState(false);
  const [communityPosts, setCommunityPosts] = useState([]);
  const [banners, setBanners] = useState([]);
  const [isBannerModalOpen, setIsBannerModalOpen] = useState(false);
  const [interestGroups, setInterestGroups] = useState([]);
  const [interestFriends, setInterestFriends] = useState([]);
  const [promoImages, setPromoImages] = useState([]);
  const [interestMedia, setInterestMedia] = useState([]); // <-- New state for InterestMedia
  const [editingArticle, setEditingArticle] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [targetedAds, setTargetedAds] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50); // Keep smaller for testing
  const [hasMoreArticles, setHasMoreArticles] = useState(true);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [categories, setCategories] = useState([]);
  const [patients, setPatients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalArticles, setTotalArticles] = useState(0);

  const { profilePicture, setProfilePicture } = useContext(UserContext);

  const debounceTimer = useRef(null);
  const sentinelRef = useRef(null);
  const loadingRef = useRef(false); // Prevents duplicate fetches

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  // Keep loadingRef synced
  useEffect(() => {
    loadingRef.current = loadingArticles;
  }, [loadingArticles]);

  useEffect(() => {
    if (window.location.search.includes('welcome=1')) {
      setShowWelcomeModal(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Fetch articles with cleaner logic
  const getArticles = useCallback(
    async (page = 1, categoryId = '', search = '', append = true) => {
      if (loadingRef.current) return;
      setLoadingArticles(true);

      try {
        const params = new URLSearchParams();
        params.append('page', page);
        params.append('pageSize', pageSize);
        params.append('featuredOnly', 'true');

        if (categoryId) params.append('categoryId', categoryId);
        if (search) params.append('search', search);

        console.log(`Fetching articles - Page: ${page}, Append: ${append}`);
        const response = await get(`/Articles?${params.toString()}`);

        if (response?.code === 200 && response?.data) {
          const fetchedArticles = response?.data?.map((item) => ({
            imageUrl: item?.featuredImagePath,
            id: item?.id,
            title: item?.title,
            subtitle: item?.fullDescription,
            description: item?.fullDescription,
            author: item?.author || "Community",
            time: new Date(item?.publishedDate)?.toLocaleDateString(),
            userId: item?.userId,
            categoryId: item?.categoryId,
            shortDescription: item?.shortDescription,
            isPublished: item?.isPublished,
            isFeatured: item?.isFeatured,
          }));

          console.log(`Fetched ${fetchedArticles.length} articles`);
          setCommunityPosts(prev => append ? [...prev, ...fetchedArticles] : fetchedArticles);

          // Better hasMore logic using totalCount when available
          const totalCount = response?.totalCount ?? null;
          if (typeof totalCount === 'number') {
            setTotalArticles(totalCount);
            setHasMoreArticles(page * pageSize < totalCount);
          } else {
            setHasMoreArticles(fetchedArticles.length === pageSize);
          }
        } else {
          setHasMoreArticles(false);
        }
      } catch (error) {
        console.error("Error fetching articles:", error);
        setHasMoreArticles(false);
      } finally {
        setLoadingArticles(false);
      }
    },
    [pageSize]
  );

  // Category change triggers reload from page 1
  const handleCategoryChange = (categoryId) => {
    console.log('Category changed to:', categoryId);
    setSelectedCategoryId(categoryId);
    setCurrentPage(1);
    setCommunityPosts([]);
    setHasMoreArticles(true);
    getArticles(1, categoryId, searchQuery, false);
  };

  // Debounced search
  const handleSearchChange = (e) => {
    const query = e.target.value || '';
    setSearchQuery(query);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      console.log('Search changed to:', query);
      setCurrentPage(1);
      setCommunityPosts([]);
      setHasMoreArticles(true);
      getArticles(1, selectedCategoryId, query, false);
    }, 500);
  };

  const clearFilters = () => {
    setSelectedCategoryId('');
    setSearchQuery('');
    setCurrentPage(1);
    setCommunityPosts([]);
    setHasMoreArticles(true);
    getArticles(1, '', '', false);
  };

 
  const getInterestMedia = useCallback(async () => {
    try {
      const response = await get('/Articles/popular-articles?topCount=10&featuredOnly=true&page=1&pageSize=10');
      if (response?.code === 200) {
        const articles = response?.data.map(item => ({
          imageUrl: item?.featuredImagePath,
          id: item?.id,
          title: item?.title,
          subtitle: item?.fullDescription,
          description: item?.fullDescription,
          author: item?.category?.name || "Community",
          time: new Date(item?.publishedDate)?.toLocaleDateString(),
        }));
        setInterestMedia(articles);
      } else {
        setInterestMedia([]);
      }
    } catch (error) {
      setInterestMedia([]);
    }
  }, []);

  // Fetch popular groups
  const getInterestGroups = async () => {
    try {
      const res = await get('/CommunityGroup/list/popular-community-group/5');
      if (res?.statusCode === 200) setInterestGroups(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  // Fetch friends list
  const getFriendsList = async () => {
    try {
      const res = await get('/friends/list');
      if (res?.statusCode === 200) setInterestFriends(res.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  // Fetch targeted ads
  const getTargetedAds = async () => {
    try {
      const userId = sessionStorage.getItem('userId');
      const pageName = 'Homepage'; // or get from current route
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/connecthealthadmin/api/ad/find-ad?Id=${userId}&page=${pageName}`);
      
      if (response.ok) {
        const data = await response.json();
        // Transform the data to match your banner structure
        const transformedAds = data.map(ad => ({
          id: ad.id,
          imageUrl: ad.bannerImageUrl,
          name: ad.title,
          description: ad.description,
          redirectUrl: ad.redirectUrl,
          displayOrder: 1,
          author: ad.adOwner?.name || '',
          company: ad.adOwner?.company || '',
          time: new Date(ad.createdAt).toLocaleTimeString(),
          startDate: ad.startDate,
          endDate: ad.endDate,
          isTargeted: true // Flag to identify targeted ads
        }));
        setTargetedAds(transformedAds);
      }
    } catch (error) {
      console.error('Error fetching targeted ads:', error);
      setTargetedAds([]);
    }
  };


  const handleArticleSubmit = async data => {
    const isEdit = editingArticle !== null;
    setIsUpdating(isEdit);

    const formData = new FormData();
    formData?.append('Title', data?.Title);
    formData?.append('ShortDescription', data?.ShortDescription);
    formData?.append('FullDescription', data?.FullDescription);
    formData?.append('CategoryId', data?.CategoryId);
    formData?.append('IsPublished', data?.IsPublished);
    formData?.append('IsFeatured', data?.IsFeatured);
    if (data?.FeaturedImage) {
      formData?.append('FeaturedImage', data?.FeaturedImage);
    }
    formData?.append('UserId', sessionStorage?.getItem('userId'));
    formData?.append('UserType', 2); // Assuming 2 for patients, adjust as needed

    try {
      let res;
      if (isEdit) {
        res = await put(`/Articles/${editingArticle?.id}`, formData);
      } else {
        res = await post('/articles', formData);
      }

      if (res.status === 'success' || res.code === 200) {
        notification({ 
          message: `Article ${isEdit ? 'updated' : 'created'} successfully`, 
          type: 'success' 
        });
        
        // Reset and refresh
        handleArticleModalClose();
        clearFilters(); // This will refresh the articles list
      } else {
        notification({ message: res?.message || 'Error', type: 'error' });
      }
    } catch (error) {
      console.error('Error submitting article:', error);
      notification({ message: 'An error occurred', type: 'error' });
    } finally {
      setIsUpdating(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const res = await gets('/Patient/patientCount');
      if (res?.code === 1) setPatients(res?.data);
    } catch (error) {
      console.error('Failed to fetch patients', error);
    }
  };


  useEffect(() => {
    getArticles(1, '', '', false);
    getTargetedAds();
    getInterestGroups();
    getInterestMedia();
    fetchPatients();
    getFriendsList();
  }, []); 

  // When currentPage increments, fetch the next page
  useEffect(() => {
    if (currentPage > 1) {
      console.log(`Loading page ${currentPage} due to scroll`);
      getArticles(currentPage, selectedCategoryId, searchQuery, true);
    }
  }, [currentPage, selectedCategoryId, searchQuery, getArticles]);

  // Sentinel observer for true infinite scroll
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasMoreArticles && !loadingRef.current) {
          console.log('Sentinel triggered - loading next page');
          setCurrentPage((p) => p + 1);
        }
      },
      { root: null, rootMargin: '200px', threshold: 0.1 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMoreArticles]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const filteredArticles = communityPosts.filter(article =>
    article.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleArticleEdit = (articleId) => {
    const article = communityPosts.find(post => post.id === articleId);
    if (article) {
      setEditingArticle(article);
      setIsArticleModalOpen(true);
    }
  };

  const handleArticleDelete = (articleId) => {
    setCommunityPosts(prevPosts => prevPosts.filter(post => post.id !== articleId));
    // Optionally refresh the entire list to ensure consistency
    setTimeout(() => {
      clearFilters();
    }, 500);
  };

  const handleArticleModalClose = () => {
    setIsArticleModalOpen(false);
    setEditingArticle(null);
    setIsUpdating(false);
  };

  return (
    <LandingErrorBoundary>
      <PageContainer key={location.key}>
        <CssBaseline />
        <Greeting profilePicture={profilePicture} name={sessionStorage.getItem('name')} />
        <LandingPageWidgets patientCount={patients} />
        
        <MainContainer>
          <TopSection>
            <TopBar />
          </TopSection>

          <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>
              Top Performing Articles For You
            </Typography>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem',
                flexWrap: isMobile ? 'wrap' : 'nowrap', // Responsive wrapping for mobile
                gap: isMobile ? '1rem' : '2rem', // Adjust spacing for mobile
              }}
            >
              <div
                style={{
                  flex: '1 1 100%',
                  maxWidth: '100%',
                  marginBottom: isMobile ? '1rem' : '0',
                }}
              >
                <InterestMedia articles={interestMedia} />
              </div>
              <div
                style={{
                  flex: '1 1 100%',
                  maxWidth: '100%',
                  marginBottom: isMobile ? '1rem' : '0',
                }}
              >
                <InterestGroups title="5 Groups that might interest you" items={interestGroups} />
              </div>
              {interestFriends?.length !== 0 && (
                <div
                  style={{
                    flex: '1 1 100%',
                    maxWidth: '100%',
                    marginBottom: isMobile ? '1rem' : '0',
                  }}
                >
                  <InterestGroups title={`${interestFriends?.length} New Friends Connection`} items={interestFriends} />
                </div>
              )}
            </div>
          </Box>

          <PromoSection sx={{ mb: 4 }}>
            <PromoBanner homePageAds={targetedAds.length > 0 ? targetedAds : null} />
          </PromoSection>

          <CommunitySection>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, pb: 1 }}>
              <Typography variant="h6" sx={{ borderBottom: 3, borderColor: 'green', pb: 1 }}>
                From the Community
              </Typography>
              <Button
                variant="contained"
                sx={{ backgroundColor: 'green', color: 'white', borderRadius: 2, p: 1 }}
                onClick={() => setIsArticleModalOpen(true)}
              >
                Create Article
              </Button>
            </Box>

            {/* Search and Filters Section */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                {/* Search Input */}
                <TextField
                  label="Search Articles"
                  variant="outlined"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search by title..."
                  sx={{ minWidth: 250 }}
                  InputLabelProps={{ shrink: true }}
                />

                {/* Category Filter */}
                <Autocomplete
                  value={categories.find(cat => cat.id === selectedCategoryId) || null}
                  onChange={(event, newValue) => {
                    handleCategoryChange(newValue ? newValue.id : '');
                  }}
                  options={categories}
                  getOptionLabel={(option) => option.name || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Category Filter"
                      placeholder="Search categories..."
                      sx={{ minWidth: 200 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                  clearText="Clear"
                  noOptionsText="No categories found"
                />

                <Button variant="outlined" onClick={clearFilters}>
                  Clear Filters
                </Button>
              </Box>
            </Box>

            {/* Articles Grid */}
            <CommunityGrid>
              {communityPosts.map((article, index) => (
                <CommunityCard
                  key={
                    article?.id
                      ? `article-${article.id}`
                      : article?.title
                        ? `article-title-${article.title}`
                        : `article-fallback-${index}`
                  }
                  {...article}
                  onEdit={handleArticleEdit}
                  onDelete={handleArticleDelete}
                />
              ))}
            </CommunityGrid>

            {/* Inline loader right under the grid */}
            {loadingArticles && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  fetching articles...
                </Typography>
              </Box>
            )}

            {/* Sentinel goes AFTER the list + loader */}
            <Box ref={sentinelRef} sx={{ height: 1 }} />

            {!loadingArticles && !hasMoreArticles && communityPosts?.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  You've reached the end! ({communityPosts.length} articles total)
                </Typography>
              </Box>
            )}

            {!loadingArticles && communityPosts?.length === 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No articles found for the selected criteria.
                </Typography>
              </Box>
            )}
          </CommunitySection>

          <HealthGoalModal open={isModalOpen} onClose={() => setIsModalOpen(false)} />
          <BannerModal open={isBannerModalOpen} onClose={() => setIsBannerModalOpen(false)} />
          <ArticleModal
            getArticles={getArticles}
            onSubmit={handleArticleSubmit}
            open={isArticleModalOpen}
            onClose={handleArticleModalClose}
            editingArticle={editingArticle}
            isLoading={isUpdating}
          />
        </MainContainer>

        {/* Welcome Modal */}
        <Dialog open={showWelcomeModal} onClose={() => setShowWelcomeModal(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, fontSize: 24, color: 'primary.main' }}>
            Welcome to Connect Health Pro Practitioners Platform!
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Your registration was successful.
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              You have a <b>free trial period of 90 days</b> to explore all features.
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: 'warning.main' }}>
              Please update your licenses and credentials on the <b>Career</b> page to complete your profile.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => { setShowWelcomeModal(false); navigate('/career'); }}
              sx={{ mt: 2 }}
            >
              Go to Career Page
            </Button>
          </DialogContent>
        </Dialog>
      </PageContainer>
    </LandingErrorBoundary>
  );
};

export default HomePage;
